// @ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Send, Mail, MessageSquare, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAdminOrgScope } from "@/hooks/useAdminOrgScope";
import { getSessionUser } from '@/lib/authSession';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image"],
    ["clean"],
  ],
};

function BannerSelect({ label, value, onChange, assets }: any) {
  const selected = assets.find((a: any) => a.id === value);
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? null : v)}>
        <SelectTrigger>
          <SelectValue placeholder="None" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">None</SelectItem>
          {assets.map((a: any) => (
            <SelectItem key={a.id} value={a.id}>
              {a.name}
              {a.advertiser_name ? ` — ${a.advertiser_name}` : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {selected?.image_url && (
        <img
          src={selected.image_url}
          alt={selected.name}
          className="mt-1 w-full max-h-20 object-contain rounded border border-border"
        />
      )}
    </div>
  );
}

const OrgEmailTemplateEditPage: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { scopeOrgId, ready } = useAdminOrgScope();
  const quillRef = useRef<any>(null);

  const [activeChannel, setActiveChannel] = useState<"email" | "sms">("email");

  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [fromName, setFromName] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [headerId, setHeaderId] = useState<string | null>(null);
  const [footerId, setFooterId] = useState<string | null>(null);
  const [topBannerId, setTopBannerId] = useState<string | null>(null);
  const [inlineBannerId, setInlineBannerId] = useState<string | null>(null);
  const [bottomBannerId, setBottomBannerId] = useState<string | null>(null);
  const [isEnabled, setIsEnabled] = useState(true);

  const [smsBody, setSmsBody] = useState("");
  const [smsEnabled, setSmsEnabled] = useState(true);

  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [addingSms, setAddingSms] = useState(false);

  const { data: template, isLoading } = useQuery({
    queryKey: ["org-message-template", templateId],
    enabled: !!templateId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("message_templates")
        .select(`
          id, key, name, description, category, is_active, organization_id,
          channels:message_template_channels(
            id, channel, is_enabled, subject, body_html, sms_body, from_name, from_email, reply_to,
            header_id, footer_id, top_banner_asset_id, inline_banner_asset_id, bottom_banner_asset_id, version
          )
        `)
        .eq("id", templateId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const orgId = template?.organization_id || null;

  const emailChannel = useMemo(
    () => (template?.channels || []).find((c: any) => c.channel === "email"),
    [template],
  );
  const smsChannel = useMemo(
    () => (template?.channels || []).find((c: any) => c.channel === "sms"),
    [template],
  );

  const { data: variables = [] } = useQuery({
    queryKey: ["org-template-variables", templateId],
    enabled: !!templateId,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("message_template_variables")
        .select("*")
        .eq("template_id", templateId)
        .order("sort_order");
      return data || [];
    },
  });

  const { data: headers = [] } = useQuery({
    queryKey: ["org-email-headers", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("message_email_headers")
        .select("id, name, is_default, html_content, image_url, link_url")
        .eq("organization_id", orgId)
        .eq("is_active", true)
        .order("sort_order");
      return data || [];
    },
  });

  const { data: footers = [] } = useQuery({
    queryKey: ["org-email-footers", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("message_email_footers")
        .select("id, name, is_default, html_content")
        .eq("organization_id", orgId)
        .eq("is_active", true)
        .order("sort_order");
      return data || [];
    },
  });

  const { data: assets = [] } = useQuery({
    queryKey: ["org-ad-assets", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("ad_assets")
        .select("id, name, advertiser_name, image_url, click_url, alt_text")
        .eq("organization_id", orgId)
        .eq("is_active", true)
        .order("name");
      return data || [];
    },
  });

  const { data: org } = useQuery({
    queryKey: ["org-name", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("organizations")
        .select("name")
        .eq("id", orgId)
        .maybeSingle();
      return data;
    },
  });

  useEffect(() => {
    if (!emailChannel) return;
    setSubject(emailChannel.subject || "");
    setBodyHtml(emailChannel.body_html || "");
    setFromName(emailChannel.from_name || "");
    setFromEmail(emailChannel.from_email || "");
    setReplyTo(emailChannel.reply_to || "");
    setHeaderId(emailChannel.header_id || null);
    setFooterId(emailChannel.footer_id || null);
    setTopBannerId(emailChannel.top_banner_asset_id || null);
    setInlineBannerId(emailChannel.inline_banner_asset_id || null);
    setBottomBannerId(emailChannel.bottom_banner_asset_id || null);
    setIsEnabled(emailChannel.is_enabled ?? true);
  }, [emailChannel?.id]);

  useEffect(() => {
    if (!smsChannel) return;
    setSmsBody(smsChannel.sms_body || "");
    setSmsEnabled(smsChannel.is_enabled ?? true);
  }, [smsChannel?.id]);

  // Default to whichever channel exists; prefer email
  useEffect(() => {
    if (!emailChannel && smsChannel) setActiveChannel("sms");
  }, [emailChannel?.id, smsChannel?.id]);

  const insertVariable = (key: string) => {
    const token = `{{${key}}}`;
    if (activeChannel === "sms") {
      setSmsBody((prev) => prev + token);
      return;
    }
    const editor = quillRef.current?.getEditor?.();
    if (editor) {
      const range = editor.getSelection(true);
      editor.insertText(range ? range.index : editor.getLength(), token);
      setBodyHtml(editor.root.innerHTML);
    } else {
      setBodyHtml((prev) => prev + token);
    }
  };

  const buildContext = () => {
    const orgName = org?.name || "RecruitLook Events";
    // Realistic sample values for known merge variables.
    const sampleDefaults: Record<string, any> = {
      coach_first_name: "Alex",
      coach_last_name: "Morgan",
      coach_name: "Alex Morgan",
      inviter_name: "Jordan Smith",
      inviter_org_name: orgName,
      organization_name: orgName,
      org_name: orgName,
      first_name: "Alex",
      last_name: "Morgan",
      team_name: "Sample Elite 16U",
      event_name: "Summer Showcase Classic",
      event_date: "August 19, 2026",
      event_dates: "August 19-21, 2026",
      event_start_date: "August 19, 2026",
      event_end_date: "August 21, 2026",
      event_location: "Sample Sports Complex, Atlanta, GA",
      division: "16U - Boys",
      age_group: "16U",
      pool: "Gold",
      accept_url: "https://recruitlookevents.com/accept-coach-invitation?id=sample",
      registration_id: "REG-100245",
      registration_amount: "750.00",
      total_amount: "750.00",
      amount_paid: "750.00",
      amount_due: "750.00",
      amount_owed: "0.00",
      payment_status: "paid",
      is_paid: true,
    };

    // Collect every {{token}} used in the current channels' content so nothing
    // renders blank, including conditional sections like {{#var}}...{{/var}}.
    const tokens = new Set<string>();
    const scan = (str: string) => {
      if (!str) return;
      const re = /\{\{\s*[#/]?\s*([a-zA-Z0-9_]+)\s*\}\}/g;
      let m;
      while ((m = re.exec(str)) !== null) tokens.add(m[1]);
    };
    scan(subject);
    scan(bodyHtml);
    scan(smsBody);

    const ctx: Record<string, any> = {};
    // Start with declared template variables.
    (variables || []).forEach((v: any) => {
      ctx[v.key] = sampleDefaults[v.key] ?? v.example_value ?? `Sample ${v.key.replace(/_/g, " ")}`;
    });
    // Then ensure every scanned token has a value.
    tokens.forEach((key) => {
      if (ctx[key] === undefined || ctx[key] === "" || ctx[key] === null) {
        ctx[key] = sampleDefaults[key] ?? `Sample ${key.replace(/_/g, " ")}`;
      }
    });
    // Always include core sample fields so org branding/conditionals render.
    return { ...sampleDefaults, ...ctx };
  };

  // Lightweight mustache-style renderer mirroring the backend output for preview.
  const renderTemplate = (str: string, ctx: Record<string, any>) => {
    if (!str) return "";
    let out = str;
    // Conditional sections {{#var}}...{{/var}}
    out = out.replace(
      /\{\{\s*#\s*([a-zA-Z0-9_]+)\s*\}\}([\s\S]*?)\{\{\s*\/\s*\1\s*\}\}/g,
      (_m, key, inner) => (ctx[key] ? inner : ""),
    );
    // Inverted sections {{^var}}...{{/var}}
    out = out.replace(
      /\{\{\s*\^\s*([a-zA-Z0-9_]+)\s*\}\}([\s\S]*?)\{\{\s*\/\s*\1\s*\}\}/g,
      (_m, key, inner) => (!ctx[key] ? inner : ""),
    );
    // Simple variables {{var}}
    out = out.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_m, key) =>
      ctx[key] != null ? String(ctx[key]) : "",
    );
    return out;
  };

  // Composite preview that mirrors how the backend assembles the email:
  // header -> top banner -> body (with inline banner) -> bottom banner -> footer.
  const previewHtml = useMemo(() => {
    const ctx = buildContext();
    const header = headers.find((h: any) => h.id === headerId);
    const footer = footers.find((f: any) => f.id === footerId);
    const topBanner = assets.find((a: any) => a.id === topBannerId);
    const inlineBanner = assets.find((a: any) => a.id === inlineBannerId);
    const bottomBanner = assets.find((a: any) => a.id === bottomBannerId);

    const bannerImg = (asset: any) => {
      if (!asset?.image_url) return "";
      const img = `<img src="${asset.image_url}" alt="${asset.alt_text || asset.name || ""}" style="display:block;width:100%;max-width:600px;height:auto;border:0;" />`;
      return asset.click_url
        ? `<a href="${asset.click_url}" target="_blank" rel="noopener noreferrer">${img}</a>`
        : img;
    };

    const parts: string[] = [];

    // Header
    if (header) {
      if (header.html_content) {
        parts.push(renderTemplate(header.html_content, ctx));
      } else if (header.image_url) {
        const himg = `<img src="${header.image_url}" alt="${header.name || ""}" style="display:block;width:100%;max-width:600px;height:auto;border:0;" />`;
        parts.push(header.link_url ? `<a href="${header.link_url}">${himg}</a>` : himg);
      }
    }

    // Top banner
    if (topBanner) parts.push(bannerImg(topBanner));

    // Body, with inline banner replacing the placeholder
    let body = renderTemplate(bodyHtml, ctx);
    const inlineHtml = inlineBanner ? bannerImg(inlineBanner) : "";
    body = body.replace(/\{\{\s*banner_placeholder\s*\}\}/g, inlineHtml);
    parts.push(`<div>${body}</div>`);

    // Bottom banner
    if (bottomBanner) parts.push(bannerImg(bottomBanner));

    // Footer
    if (footer?.html_content) parts.push(renderTemplate(footer.html_content, ctx));

    return parts.join("\n");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bodyHtml,
    headerId,
    footerId,
    topBannerId,
    inlineBannerId,
    bottomBannerId,
    headers,
    footers,
    assets,
    variables,
    org?.name,
  ]);

  const handleSaveEmail = async () => {
    if (!emailChannel) {
      toast.error("This template has no email channel.");
      return;
    }
    setSaving(true);
    try {
      const { error } = await (supabase as any)
        .from("message_template_channels")
        .update({
          subject,
          body_html: bodyHtml,
          from_name: fromName || null,
          from_email: fromEmail || null,
          reply_to: replyTo || null,
          header_id: headerId,
          footer_id: footerId,
          top_banner_asset_id: topBannerId,
          inline_banner_asset_id: inlineBannerId,
          bottom_banner_asset_id: bottomBannerId,
          is_enabled: isEnabled,
          version: (emailChannel.version || 1) + 1,
          last_modified_at: new Date().toISOString(),
        })
        .eq("id", emailChannel.id);
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ["org-message-template", templateId] });
      toast.success("Email template saved");
    } catch (err: any) {
      toast.error("Save failed: " + (err?.message || "unknown"));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSms = async () => {
    if (!smsChannel) {
      toast.error("This template has no SMS channel.");
      return;
    }
    setSaving(true);
    try {
      const { error } = await (supabase as any)
        .from("message_template_channels")
        .update({
          sms_body: smsBody,
          is_enabled: smsEnabled,
          version: (smsChannel.version || 1) + 1,
          last_modified_at: new Date().toISOString(),
        })
        .eq("id", smsChannel.id);
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ["org-message-template", templateId] });
      toast.success("SMS template saved");
    } catch (err: any) {
      toast.error("Save failed: " + (err?.message || "unknown"));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => (activeChannel === "sms" ? handleSaveSms() : handleSaveEmail());

  const handleAddSms = async () => {
    setAddingSms(true);
    try {
      const { error } = await (supabase as any)
        .from("message_template_channels")
        .insert({
          template_id: templateId,
          channel: "sms",
          is_enabled: true,
          sms_body: "",
        });
      if (error) throw error;
      await queryClient.invalidateQueries({ queryKey: ["org-message-template", templateId] });
      setActiveChannel("sms");
      toast.success("SMS version added");
    } catch (err: any) {
      toast.error("Could not add SMS: " + (err?.message || "unknown"));
    } finally {
      setAddingSms(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!subject.trim()) {
      toast.error("Add a subject before sending a test.");
      return;
    }
    setSendingTest(true);
    try {
      const { data: { user } } = await getSessionUser();
      if (!user?.email) {
        toast.error("No email on the logged-in user.");
        return;
      }
      const { error } = await (supabase as any).functions.invoke("send-system-message", {
        body: {
          template_key: template.key,
          organization_id: orgId,
          channels: ["email"],
          recipient: { email: user.email, user_id: user.id, name: "Test Recipient" },
          context: buildContext(),
          override_subject: subject,
          override_body_html: bodyHtml,
          override_header_id: headerId,
          override_footer_id: footerId,
          override_top_banner_asset_id: topBannerId,
          override_inline_banner_asset_id: inlineBannerId,
          override_bottom_banner_asset_id: bottomBannerId,
        },
      });
      if (error) throw error;
      toast.success(`Test email sent to ${user.email}`);
    } catch (err: any) {
      toast.error("Test send failed: " + (err?.message || "unknown"));
    } finally {
      setSendingTest(false);
    }
  };

  const handleSendTestSms = async () => {
    if (!smsBody.trim()) {
      toast.error("Add SMS text before sending a test.");
      return;
    }
    setSendingTest(true);
    try {
      const { data: { user } } = await getSessionUser();
      if (!user?.id) {
        toast.error("Not signed in.");
        return;
      }
      const { data: phoneRow } = await (supabase as any)
        .from("user_phone_numbers")
        .select("phone_e164")
        .eq("user_id", user.id)
        .order("is_primary", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!phoneRow?.phone_e164) {
        toast.error("No phone number on file for your account. Add one in your profile first.");
        return;
      }
      const { error } = await (supabase as any).functions.invoke("send-system-message", {
        body: {
          template_key: template.key,
          organization_id: orgId,
          channels: ["sms"],
          recipient: { phone: phoneRow.phone_e164, user_id: user.id, name: "Test Recipient" },
          context: buildContext(),
          override_sms_body: smsBody,
        },
      });
      if (error) throw error;
      toast.success(`Test SMS sent to ${phoneRow.phone_e164}`);
    } catch (err: any) {
      toast.error("Test send failed: " + (err?.message || "unknown"));
    } finally {
      setSendingTest(false);
    }
  };

  const handleSendTest = () =>
    activeChannel === "sms" ? handleSendTestSms() : handleSendTestEmail();

  const smsLength = smsBody.length;
  const smsSegments = Math.max(1, Math.ceil(smsLength / 160));

  if (isLoading || !ready) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading...
      </div>
    );
  }

  if (!template) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate("/admin-dashboard/communication/templates")}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <p className="text-muted-foreground">Template not found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Button
            variant="ghost"
            size="sm"
            className="mb-2 -ml-2"
            onClick={() => navigate("/admin-dashboard/communication/templates")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to templates
          </Button>
          <h1 className="text-3xl font-bold tracking-tight">{template.name}</h1>
          <p className="text-muted-foreground font-mono text-sm">{template.key}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" onClick={handleSendTest} disabled={sendingTest}>
            {sendingTest ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            {activeChannel === "sms" ? "Send test SMS" : "Send test email"}
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      <Tabs value={activeChannel} onValueChange={(v) => setActiveChannel(v as any)}>
        <TabsList>
          <TabsTrigger value="email">
            <Mail className="h-4 w-4 mr-2" /> Email
          </TabsTrigger>
          <TabsTrigger value="sms">
            <MessageSquare className="h-4 w-4 mr-2" /> SMS
          </TabsTrigger>
        </TabsList>

        {/* EMAIL TAB */}
        <TabsContent value="email" className="mt-6">
          {!emailChannel ? (
            <p className="text-muted-foreground">This template has no email channel.</p>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
              <div className="space-y-4 min-w-0">
                <Card>
                  <CardContent className="pt-6 space-y-4">
                    <div className="flex items-center gap-3">
                      <Switch checked={isEnabled} onCheckedChange={setIsEnabled} />
                      <Label>Email channel enabled</Label>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Subject</Label>
                      <Input
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="e.g. Welcome to {{event_name}}"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Body</Label>
                      <div className="bg-white text-black rounded-md border border-border overflow-hidden">
                        <ReactQuill
                          ref={quillRef}
                          theme="snow"
                          value={bodyHtml}
                          onChange={setBodyHtml}
                          modules={QUILL_MODULES}
                          style={{ minHeight: 280 }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Insert <code>{`{{variable}}`}</code> tokens and conditional sections like{" "}
                        <code>{`{{#var}}...{{/var}}`}</code>. Place{" "}
                        <code>{`{{banner_placeholder}}`}</code> where the inline banner should appear.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Sender</CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-1.5">
                      <Label>From name</Label>
                      <Input value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="RecruitLook Events" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>From email</Label>
                      <Input value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} placeholder="noreply@..." />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Reply-to</Label>
                      <Input value={replyTo} onChange={(e) => setReplyTo(e.target.value)} placeholder="support@..." />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Available variables</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {variables.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No variables declared for this template.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {variables.map((v: any) => (
                          <button
                            key={v.id}
                            type="button"
                            title={v.description || v.label}
                            onClick={() => insertVariable(v.key)}
                            className="rounded border border-primary/30 bg-primary/10 px-2.5 py-1 text-xs font-mono text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
                          >
                            {`{{${v.key}}}`}
                          </button>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-4 lg:sticky lg:top-6 lg:self-start">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Branding</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-1.5">
                      <Label>Header</Label>
                      <Select value={headerId || "none"} onValueChange={(v) => setHeaderId(v === "none" ? null : v)}>
                        <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {headers.map((h: any) => (
                            <SelectItem key={h.id} value={h.id}>
                              {h.name}{h.is_default ? " (default)" : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Footer</Label>
                      <Select value={footerId || "none"} onValueChange={(v) => setFooterId(v === "none" ? null : v)}>
                        <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {footers.map((f: any) => (
                            <SelectItem key={f.id} value={f.id}>
                              {f.name}{f.is_default ? " (default)" : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Email banners</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <BannerSelect label="Top banner" value={topBannerId} onChange={setTopBannerId} assets={assets} />
                    <BannerSelect label="Inline banner ({{banner_placeholder}})" value={inlineBannerId} onChange={setInlineBannerId} assets={assets} />
                    <BannerSelect label="Bottom banner" value={bottomBannerId} onChange={setBottomBannerId} assets={assets} />
                    {assets.length === 0 && (
                      <p className="text-xs text-muted-foreground">
                        No ad assets for this organization yet. Add them under Advertising.
                      </p>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Live preview</CardTitle>
                    <p className="text-xs text-muted-foreground">
                      Composited exactly like the delivered email: header, banners, body and footer.
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="rounded-md border border-border bg-white text-black overflow-hidden">
                      <div
                        className="mx-auto"
                        style={{ maxWidth: 600 }}
                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </TabsContent>

        {/* SMS TAB */}
        <TabsContent value="sms" className="mt-6">
          {!smsChannel ? (
            <Card>
              <CardContent className="py-12 flex flex-col items-center justify-center text-center gap-3">
                <MessageSquare className="h-8 w-8 text-muted-foreground" />
                <p className="text-muted-foreground">
                  This template has no SMS version yet.
                </p>
                <Button onClick={handleAddSms} disabled={addingSms}>
                  {addingSms ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                  Add SMS version
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
              <div className="space-y-4 min-w-0">
                <Card>
                  <CardContent className="pt-6 space-y-4">
                    <div className="flex items-center gap-3">
                      <Switch checked={smsEnabled} onCheckedChange={setSmsEnabled} />
                      <Label>SMS enabled</Label>
                    </div>
                    <div className="space-y-1.5">
                      <Label>SMS message</Label>
                      <Textarea
                        value={smsBody}
                        onChange={(e) => setSmsBody(e.target.value)}
                        placeholder="e.g. {{event_name}} starts soon! See you at {{venue_name}}."
                        rows={6}
                        className="font-mono text-sm"
                      />
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>
                          Supports <code>{`{{variable}}`}</code> and{" "}
                          <code>{`{{#var}}...{{/var}}`}</code> sections.
                        </span>
                        <span>
                          {smsLength} chars · {smsSegments} segment{smsSegments > 1 ? "s" : ""}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      SMS sends from the shared platform number.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Available variables</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {variables.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No variables declared for this template.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {variables.map((v: any) => (
                          <button
                            key={v.id}
                            type="button"
                            title={v.description || v.label}
                            onClick={() => insertVariable(v.key)}
                            className="rounded border border-primary/30 bg-primary/10 px-2.5 py-1 text-xs font-mono text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
                          >
                            {`{{${v.key}}}`}
                          </button>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-4 lg:sticky lg:top-6 lg:self-start">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Preview</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="rounded-2xl bg-muted px-4 py-3 text-sm whitespace-pre-wrap break-words">
                      {smsBody || <span className="text-muted-foreground">Your SMS will appear here.</span>}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default OrgEmailTemplateEditPage;
